import { useQuery } from "@tanstack/react-query";
import { Button } from "@/design-system/Button";
import { ArrowDownToLine, ArrowUpFromLine, Ban } from "lucide-react";
import { useState } from "react";

import { exitText, reasonText } from "@/shared/text/reasons";
import { readHistory, type CycleResponse, type HistoryResponse, type SlotResponse } from "@/shared/api";
import { signedMoney } from "@/shared/money/money";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

/** One glyph per kind, so a scanning reader sorts the feed by shape. */
const ICON: Record<"opened" | "closed" | "error", React.JSX.Element> = {
  opened: (
    <ArrowDownToLine aria-hidden="true" className="size-4 shrink-0 text-action-primary" />
  ),
  closed: (
    <ArrowUpFromLine aria-hidden="true" className="size-4 shrink-0 text-status-positive" />
  ),
  error: <Ban aria-hidden="true" className="size-4 shrink-0 text-status-warning" />,
};

type Activity = {
  readonly id: string;
  readonly at: string;
  readonly kind: "opened" | "closed" | "error";
  readonly label: string;
  readonly detail: string;
};

function recentHistory(): ReturnType<typeof useQuery<HistoryResponse>> {
  return useQuery({
    queryKey: ["overview", "recent-closed-cycles"],
    queryFn: async (): Promise<HistoryResponse> => {
      const { data, error } = await readHistory({
        query: { limit: 5, offset: 0, sort: "closed_at", order: "desc" },
      });
      if (error !== undefined || data === undefined) throw new Error("recent history unreadable");
      return data;
    },
    retry: false,
  });
}

function opened(slot: SlotResponse): Activity | null {
  if (slot.opened_at == null || slot.symbol == null) return null;
  return {
    id: `opened-${slot.slot_id}-${slot.opened_at}`,
    at: slot.opened_at,
    kind: "opened",
    label: `Цикл открыт · ${slot.symbol}`,
    // The pool the cycle trades in, not the physical slot it was cut from.
    detail: slot.logical_pool ?? slot.pool,
  };
}

/**
 * A blocked slot, as an event.
 *
 * The errors lens has to have something to filter, and a refusal is the one
 * kind of event this feed can see: the runtime records why a slot was stopped,
 * and that is the operator's cue to look.
 */
function blocked(slot: SlotResponse): Activity | null {
  if (slot.blocked_reason === null || slot.opened_at == null) return null;
  return {
    id: `blocked-${slot.slot_id}`,
    at: slot.opened_at,
    kind: "error",
    label: `Отказ · ${slot.symbol ?? slot.slot_id}`,
    detail: reasonText(slot.blocked_reason),
  };
}

function closed(cycle: CycleResponse): Activity {
  return {
    id: `closed-${cycle.symbol}-${cycle.closed_at}`,
    at: cycle.closed_at,
    kind: "closed",
    label: `Цикл закрыт · ${cycle.symbol}`,
    // The pool is dropped rather than printed as "undefined" when a payload
    // predates it. A word the reader cannot act on is worse than a shorter
    // line, and this one appeared on screen.
    detail: [cycle.pool, `${signedMoney(cycle.net)} NET`, exitText(cycle.exit_reason)]
      .filter((part) => typeof part === "string" && part.length > 0)
      .join(" · "),
  };
}

function time(value: string): string {
  return new Date(value).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

type Lens = "all" | "opened" | "closed" | "errors";

const LENSES: readonly { readonly id: Lens; readonly label: string }[] = [
  { id: "all", label: "Выборка" },
  { id: "opened", label: "Открытия" },
  { id: "closed", label: "Закрытия" },
  { id: "errors", label: "Блокировки" },
];

/** Which events a lens admits. */
function holds(lens: Lens, item: Activity): boolean {
  if (lens === "all") return true;
  if (lens === "errors") return item.kind === "error";
  return item.kind === lens;
}

export function BotActivity({
  slots,
}: {
  readonly slots: readonly SlotResponse[];
}): React.JSX.Element {
  const [lens, setLens] = useState<Lens>("all");
  const history = recentHistory();

  const all = [
    ...slots.map(opened).filter((item): item is Activity => item !== null),
    ...slots.map(blocked).filter((item): item is Activity => item !== null),
    ...(history.data?.cycles.map(closed) ?? []),
  ].sort((left, right) => Date.parse(right.at) - Date.parse(left.at));

  const activity = all.filter((item) => holds(lens, item)).slice(0, 5);

  return (
    <section
      aria-label="Активность бота"
      className="flex h-full min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={`${PANEL_HEADER} flex-wrap justify-between gap-x-3 gap-y-1`}>
        <h2 className={PANEL_TITLE}>Активность бота</h2>
        <div role="group" aria-label="Фильтр событий" className="flex flex-wrap gap-0.5">
          {LENSES.map((option) => {
            const size = all.filter((item) => holds(option.id, item)).length;
            const active = option.id === lens;
            return (
              <Button
                key={option.id}
                variant="ghost"
                size="sm"
                selected={active}
                aria-pressed={active}
                disabled={size === 0 && !active}
                data-testid={`activity-lens-${option.id}`}
                onClick={() => {
                  setLens(option.id);
                }}
                className="min-h-6 gap-1 whitespace-nowrap"
              >
                {option.label}
                <span className="tabular-nums text-text-muted">{size}</span>
              </Button>
            );
          })}
        </div>
      </div>

      <p className="px-3 py-2 text-helper-sm text-text-secondary" data-testid="activity-scope">
        Открытия текущих слотов + до 5 последних закрытий. Блокировки — текущее состояние слотов, не журнал ошибок. Показаны до 5 событий выбранной категории.
      </p>
      {(history.isError || history.isPending || history.isPaused) && <p role="status" className="px-3 pb-2 text-helper-sm text-text-secondary">
        {history.isPending && !history.isPaused ? "История закрытий загружается" : "История закрытий недоступна; выборка неполна"}
      </p>}
      {activity.length === 0 ? (
        <p className="flex-1 border-t border-border-subtle px-3 py-3 text-helper-sm text-text-secondary">
          {lens === "all"
            ? "Зафиксированных событий пока нет"
            : "Под этот фильтр не попадает ни одно событие"}
        </p>
      ) : (
        <ul className="flex-1 divide-y divide-border-subtle border-t border-border-subtle px-3">
          {activity.map((item) => (
            <li key={item.id} className="flex min-w-0 items-center gap-3 py-2">
              {ICON[item.kind]}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-main text-text-primary">{item.label}</span>
                <span className="block truncate text-helper-sm text-text-secondary">
                  {item.detail}
                </span>
              </span>
              {item.kind === "error" ? <span className="text-helper-sm text-text-muted">Сейчас</span> : <time
                dateTime={item.at}
                className="shrink-0 text-helper-sm tabular-nums text-text-muted"
              >
                {time(item.at)}
              </time>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
